import {
  FC,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import ReactPlayer from 'react-player/lazy';
import styled from 'styled-components';
import { OnProgressProps } from 'react-player/base';

import { APP_ROUTES } from '@navigation/types';
import { showApi, useLazyGetRecordedShowByIdQuery } from '@redux/queries/show';
import { useCurrentRecordedShowId } from '@redux/selectors/shows';
import { useAppDispatch } from '@redux/store';
import { ParticipantPosition, TeamType } from '@typings/participant';
import { RecordedShowServerData, ChatMessageServerData } from '@typings/show';
import { AlertType, VIDEO_FILE_EXTENSION } from '@utils/constants';
import { showsSlice } from '@redux/state/showsSlice';
import { chatSlice } from '@redux/state/chatSlice';
import { usersSlice } from '@redux/state/usersSlice';
import RecordedShowMiniPlayer from '@components/miniPlayer/RecordedShowMiniPlayer';
import { useCurrentUser } from '@redux/selectors/users';
import CustomAudioPlayer from '@components/miniPlayer/components/CustomAudioPlayer';

import socket from '../services/socket.service';

import { useAlert } from './AlertProvider';

const Container = styled.div<{ $hidden: boolean }>`
  width: 60%;
  position: fixed;
  bottom: 0;
  left: 0;
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
  overflow: visible;
  z-index: 10;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
  }
`;

const HiddenPlayer = styled.div`
  display: none;
`;

type Params = {
  children: ReactNode;
};

type Topic = {
  index: number;
  id: string;
  title: string;
  image: string | null;
  timeout: number;
};

type Speaker = {
  index: number;
  id: string;
  startTimeout: number;
  endTimeout: number;
};

type RecordedShowContextParams = {
  currentRecordedShowId: string | null;
  recordedShowData: RecordedShowServerData | null | undefined;
  topics: Topic[];
  currentTopicIndex: number;
  currentLeftSpeakerIndex: number | null;
  currentRightSpeakerIndex: number | null;
  speakers: {
    left: Speaker[];
    right: Speaker[];
  };
  source: string | null;
  isLoading?: boolean;
  isError?: boolean;
  closeCurrentRecordedShow: () => void;
};

const RecordedShowContext = createContext<RecordedShowContextParams>({
  currentRecordedShowId: null,
  recordedShowData: null,
  topics: [],
  currentTopicIndex: 0,
  currentLeftSpeakerIndex: null,
  currentRightSpeakerIndex: null,
  speakers: { left: [], right: [] },
  source: null,
  isLoading: false,
  isError: false,
  closeCurrentRecordedShow: () => {},
});

const INITIAL_RATE = 1;

export const useRecordedShow = () => useContext(RecordedShowContext);

export const RecordedShowProvider: FC<Params> = ({ children }) => {
  const [isError, setIsError] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(true);
  const [rate, setRate] = useState<number>(INITIAL_RATE);
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [currentTopicIndex, setCurrentTopicIndex] = useState<number>(0);
  const [currentLeftSpeakerIndex, setCurrentLeftSpeakerIndex] = useState<
    number | null
  >(null);
  const [currentRightSpeakerIndex, setCurrentRightSpeakerIndex] = useState<
    number | null
  >(null);
  const [span, setSpan] = useState<number | null>(null);
  const currentRecordedShowId = useCurrentRecordedShowId();
  const currentUser = useCurrentUser();
  const audioRef = useRef<ReactPlayer>(null);
  const { showAlert } = useAlert();

  const [
    getRecordedShow,
    {
      data: recordedShowData,
      isFetching,
      isSuccess,
      isError: isGettingRecordedShowError,
    },
  ] = useLazyGetRecordedShowByIdQuery();
  const topics = useMemo(() => {
    if (span !== null && !!recordedShowData?.topics?.length) {
      return [...(recordedShowData?.topics || [])].map((topic, index) => {
        return {
          index,
          id: topic.id,
          title: topic.title,
          image: topic.image,
          timeout:
            index === 0
              ? 0
              : !!topic.createdAt && !!recordedShowData?.createdDate
                ? (new Date(topic.createdAt).getTime() -
                    new Date(recordedShowData.createdDate).getTime()) /
                    1000 -
                  span
                : -1,
        };
      });
    }

    return [];
  }, [recordedShowData?.topics, recordedShowData?.createdDate, span]);
  const speakers = useMemo(() => {
    if (span !== null && !!recordedShowData?.speakers?.length) {
      return [...(recordedShowData?.speakers || [])]
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        )
        .reduce(
          (
            acc: {
              left: Speaker[];
              right: Speaker[];
            },
            speaker,
          ) => {
            const key =
              speaker.side === TeamType.Blue
                ? ParticipantPosition.Left
                : ParticipantPosition.Right;
            acc[key].push({
              index: acc[key].length,
              id: speaker.id,
              startTimeout:
                !!speaker.createdAt && !!recordedShowData?.createdDate
                  ? (new Date(speaker.createdAt).getTime() -
                      new Date(recordedShowData.createdDate).getTime()) /
                      1000 -
                    span
                  : -1,
              endTimeout:
                !!speaker.endedAt && !!recordedShowData?.createdDate
                  ? (new Date(speaker.endedAt).getTime() -
                      new Date(recordedShowData.createdDate).getTime()) /
                      1000 -
                    span
                  : -1,
            });

            return acc;
          },
          { left: [], right: [] },
        );
    }

    return { left: [], right: [] };
  }, [recordedShowData?.speakers, recordedShowData?.createdDate, span]);
  const source = useMemo(() => {
    if (recordedShowData?.id && recordedShowData?.sid) {
      return `${process.env.REACT_APP_BUCKET_URL}/shows/${recordedShowData.id.replace(
        /-/g,
        '',
      )}/audio/${recordedShowData.sid}_${
        recordedShowData.id
      }${VIDEO_FILE_EXTENSION}`;
    }

    return null;
  }, [recordedShowData?.id, recordedShowData?.sid]);
  const dispatch = useAppDispatch();

  const onError = useCallback(
    (err: string) => {
      console.error(`Video cannot be loaded: ${source}`, err);
      setIsError(true);
      showAlert(AlertType.Error, 'Recorded show cannot be loaded!');
    },
    [source, showAlert],
  );

  const onEnd = useCallback(() => {
    setCurrentTopicIndex(0);
    setCurrentLeftSpeakerIndex(null);
    setCurrentRightSpeakerIndex(null);
    setIsPaused(true);
    setSliderValue(0);
    audioRef.current?.seekTo(0);
  }, []);

  const onProgress = useCallback(
    (data: OnProgressProps) => {
      setSliderValue((data.playedSeconds / duration) * 100);

      const currentTime = Math.floor(data.playedSeconds);
      let nextTopicIndex = currentTopicIndex;
      let nextLeftSpeakerIndex = currentLeftSpeakerIndex;
      let nextRightSpeakerIndex = currentRightSpeakerIndex;
      if (
        currentTopicIndex < topics.length - 1 &&
        currentTime >= topics[currentTopicIndex + 1].timeout
      ) {
        nextTopicIndex = currentTopicIndex + 1;
      }

      if (currentLeftSpeakerIndex !== null) {
        if (currentTime >= speakers.left[currentLeftSpeakerIndex]?.endTimeout) {
          nextLeftSpeakerIndex = null;
        }
        if (
          currentLeftSpeakerIndex < speakers.left.length - 1 &&
          currentTime >=
            speakers.left[currentLeftSpeakerIndex + 1]?.startTimeout
        ) {
          nextLeftSpeakerIndex = currentLeftSpeakerIndex + 1;
        }
      } else {
        const nextLeftSpeaker = speakers.left.find(
          (speaker) => speaker.startTimeout === currentTime,
        )?.index;
        nextLeftSpeakerIndex =
          nextLeftSpeaker !== undefined ? nextLeftSpeaker : null;
      }

      if (currentRightSpeakerIndex !== null) {
        if (
          currentTime >= speakers.right[currentRightSpeakerIndex]?.endTimeout
        ) {
          nextRightSpeakerIndex = null;
        }
        if (
          currentRightSpeakerIndex < speakers.right.length - 1 &&
          currentTime >=
            speakers.right[currentRightSpeakerIndex + 1]?.startTimeout
        ) {
          nextRightSpeakerIndex = currentRightSpeakerIndex + 1;
        }
      } else {
        const nextRightSpeaker = speakers.right.find(
          (speaker) => speaker.startTimeout === currentTime,
        )?.index;
        nextRightSpeakerIndex =
          nextRightSpeaker !== undefined ? nextRightSpeaker : null;
      }

      if (nextTopicIndex !== currentTopicIndex) {
        setCurrentTopicIndex(nextTopicIndex);
      }

      if (nextLeftSpeakerIndex !== currentLeftSpeakerIndex) {
        setCurrentLeftSpeakerIndex(nextLeftSpeakerIndex);
      }
      if (nextRightSpeakerIndex !== currentRightSpeakerIndex) {
        setCurrentRightSpeakerIndex(nextRightSpeakerIndex);
      }
    },
    [
      currentTopicIndex,
      currentLeftSpeakerIndex,
      currentRightSpeakerIndex,
      topics,
      speakers.left,
      speakers.right,
      duration,
    ],
  );

  const onDuration = useCallback(() => {
    const audioDuration = audioRef.current?.getDuration();
    if (audioDuration) {
      setDuration(audioDuration);
    }
  }, []);

  const getTopicAndSpeakersOnSeek = useCallback(
    (time: number) => {
      const nextTopicIndex =
        topics.find(
          (topic) =>
            topic.timeout <= time &&
            (topic.index === topics.length - 1 ||
              topics[topic.index + 1]?.timeout > time),
        )?.index || 0;
      const nextLeftSpeaker = speakers?.left.find(
        (speaker) =>
          speaker.startTimeout <= time &&
          (speaker.index === speakers.left.length - 1 ||
            speakers.left[speaker.index + 1]?.startTimeout > time),
      );
      const nextLeftSpeakerIndex =
        nextLeftSpeaker !== undefined ? nextLeftSpeaker.index : null;
      const nextRightSpeaker = speakers?.right.find(
        (speaker) =>
          speaker.startTimeout <= time &&
          (speaker.index === speakers.right.length - 1 ||
            speakers.right[speaker.index + 1]?.startTimeout > time),
      );
      const nextRightSpeakerIndex =
        nextRightSpeaker !== undefined ? nextRightSpeaker.index : null;

      return { nextTopicIndex, nextLeftSpeakerIndex, nextRightSpeakerIndex };
    },
    [topics, speakers?.left, speakers?.right],
  );

  const handleSeek = useCallback(
    (value: number | number[]) => {
      const percentage = Array.isArray(value) ? value[0] : value;
      if (!audioRef.current) {
        return;
      }

      const fraction = percentage / 100;
      audioRef.current.seekTo(fraction, 'fraction');
      const seekTime = Math.floor(fraction * duration);
      const { nextTopicIndex, nextLeftSpeakerIndex, nextRightSpeakerIndex } =
        getTopicAndSpeakersOnSeek(seekTime);

      setCurrentTopicIndex(nextTopicIndex);
      setCurrentLeftSpeakerIndex(nextLeftSpeakerIndex);
      setCurrentRightSpeakerIndex(nextRightSpeakerIndex);
    },
    [getTopicAndSpeakersOnSeek, duration],
  );

  const handlePlay = useCallback(() => {
    if (sliderValue === 0 && audioRef.current) {
      audioRef.current.seekTo(0);
    }
    setIsPaused(false);
  }, [sliderValue]);

  const handlePause = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleSpeedChange = useCallback((speed: number) => {
    setRate(speed);
  }, []);

  const closeCurrentRecordedShow = useCallback(() => {
    dispatch(showsSlice.actions.setCurrentRecordedShowId(null));
    dispatch(showApi.util.resetApiState());
    dispatch(chatSlice.actions.clearChatMessages());
    setIsError(false);
    setDuration(0);
    setIsPaused(true);
    setRate(INITIAL_RATE);
    setSliderValue(0);
    setSpan(null);
    setCurrentTopicIndex(0);
    setCurrentLeftSpeakerIndex(null);
    setCurrentRightSpeakerIndex(null);
    audioRef.current?.seekTo(0);
  }, [dispatch]);

  const setChatMessageData = useCallback(
    (chatMessageData: ChatMessageServerData) => {
      if (chatMessageData && currentUser?.id !== chatMessageData.user.id) {
        const { show: showData, user, ...message } = chatMessageData;
        dispatch(
          usersSlice.actions.setUserDataById({
            id: user.id,
            name: user.name,
            usernameColor: user.settings?.usernameColor,
          }),
        );
        dispatch(
          chatSlice.actions.setChatMessageById({
            ...message,
            showId: showData.id,
            userId: user.id,
          }),
        );
        dispatch(
          chatSlice.actions.addMessageToShowIds({
            showId: showData.id,
            id: message.id,
          }),
        );
      }
    },
    [dispatch, currentUser?.id],
  );

  useEffect(() => {
    if (currentRecordedShowId) {
      getRecordedShow({ id: currentRecordedShowId });
    }
  }, [currentRecordedShowId, getRecordedShow]);

  useEffect(() => {
    if (!currentRecordedShowId) {
      return;
    }

    if (
      (isSuccess && (!recordedShowData || !source)) ||
      isGettingRecordedShowError
    ) {
      setIsError(true);
    }
    if ((isSuccess && !recordedShowData?.id) || isGettingRecordedShowError) {
      dispatch(showsSlice.actions.setCurrentRecordedShowId(null));
    }
  }, [
    currentRecordedShowId,
    isSuccess,
    source,
    recordedShowData,
    isGettingRecordedShowError,
    dispatch,
  ]);

  useEffect(() => {
    if (!duration) {
      return;
    }

    if (recordedShowData?.updatedDate && recordedShowData?.createdDate) {
      const serverDuration =
        (new Date(recordedShowData?.updatedDate).getTime() -
          new Date(recordedShowData?.createdDate).getTime()) /
        1000;
      if (duration < serverDuration) {
        setSpan(Math.floor((serverDuration - duration) / 1.5));
      } else {
        setSpan(0);
      }
    }
  }, [recordedShowData?.createdDate, recordedShowData?.updatedDate, duration]);

  useEffect(() => {
    if (!currentRecordedShowId) {
      return;
    }

    socket.on(
      `recorded-chat-message-${currentRecordedShowId}`,
      (payload: ChatMessageServerData) => {
        setChatMessageData(payload);
      },
    );

    return () => {
      socket.off(`recorded-chat-message-${currentRecordedShowId}`);
    };
  }, [currentRecordedShowId, setChatMessageData]);

  useEffect(() => {
    const currentAudioRef = audioRef.current;

    return () => {
      if (currentAudioRef) {
        currentAudioRef.seekTo(0);
      }
    };
  }, []);

  return (
    <RecordedShowContext.Provider
      value={{
        currentRecordedShowId,
        closeCurrentRecordedShow,
        recordedShowData,
        topics,
        currentTopicIndex,
        currentLeftSpeakerIndex,
        currentRightSpeakerIndex,
        speakers,
        source,
        isLoading: isFetching,
        isError,
      }}
    >
      {children}

      {!!recordedShowData?.id &&
        createPortal(
          <>
            {source ? (
              <HiddenPlayer>
                <ReactPlayer
                  ref={audioRef}
                  url={source}
                  playing={!isPaused}
                  playbackRate={rate}
                  onError={onError}
                  onEnded={onEnd}
                  onProgress={onProgress}
                  onDuration={onDuration}
                  progressInterval={1000}
                  width='0'
                  height='0'
                  config={{
                    file: {
                      forceHLS: true,
                      hlsOptions: {
                        enableWorker: true,
                        lowLatencyMode: false,
                        maxBufferLength: 30,
                        maxMaxBufferLength: 600,
                        maxBufferSize: 60 * 1000 * 1000,
                        maxBufferHole: 0.5,
                      },
                      attributes: {
                        crossOrigin: 'anonymous',
                        preload: 'metadata',
                      },
                    },
                  }}
                />
              </HiddenPlayer>
            ) : null}

            <Container
              $hidden={!location.pathname.includes(APP_ROUTES.SHOW_RECORDED)}
            >
              {!isError && (
                <CustomAudioPlayer
                  showId={recordedShowData.id}
                  isPaused={isPaused}
                  duration={duration}
                  sliderValue={sliderValue}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onSeek={handleSeek}
                  onSpeedChange={handleSpeedChange}
                  rate={rate}
                  isDisabled={!source}
                />
              )}
            </Container>
          </>,
          document.body,
        )}

      <RecordedShowMiniPlayer />
    </RecordedShowContext.Provider>
  );
};
